import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatRoleName } from '../../utils/roleFormatter';
import { fetchHelpArticleMarkdown, fetchHelpManifest } from './helpApi';
import MarkdownHelpViewer from './MarkdownHelpViewer';
import {
  filterArticlesByQuery,
  findArticleById,
  groupAllArticles,
  groupPlaybookArticles,
  parseHelpArticleContent,
  resolvePlaybookArticleIds,
} from './helpPlaybook';
import './HelpPage.css';

const TAB_FOR_YOU = 'for-you';
const TAB_ALL = 'all';

/** @typedef {{ type: 'index', activeTab: string, searchQuery: string }} HelpIndexNavEntry */
/** @typedef {{ type: 'article', articleId: string }} HelpArticleNavEntry */
/** @typedef {HelpIndexNavEntry | HelpArticleNavEntry} HelpNavEntry */

function HelpPage({ user, initialArticleId = null }) {
  const [manifest, setManifest] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_FOR_YOU);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState(initialArticleId);
  const [articleMarkdown, setArticleMarkdown] = useState('');
  const [articleLoading, setArticleLoading] = useState(false);
  const [articleError, setArticleError] = useState(null);
  const [scrollToSection, setScrollToSection] = useState(null);
  const navStackRef = useRef(/** @type {HelpNavEntry[]} */ ([]));

  const role = user?.role || null;
  const roleLabel = role ? formatRoleName(role) : 'User';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchHelpManifest();
        if (!cancelled) {
          setManifest(data);
          setLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load help content.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const playbookIds = useMemo(
    () => resolvePlaybookArticleIds(manifest, role),
    [manifest, role]
  );

  const forYouGroups = useMemo(
    () => groupPlaybookArticles(manifest, playbookIds, searchQuery),
    [manifest, playbookIds, searchQuery]
  );

  const allGroups = useMemo(() => {
    if (!manifest) return [];
    return groupAllArticles(manifest).map((group) => ({
      ...group,
      articles: filterArticlesByQuery(group.articles, searchQuery),
    })).filter((g) => g.articles.length > 0);
  }, [manifest, searchQuery]);

  const selectedArticle = useMemo(
    () => findArticleById(manifest, selectedArticleId),
    [manifest, selectedArticleId]
  );

  const parsedArticle = useMemo(
    () => parseHelpArticleContent(articleMarkdown),
    [articleMarkdown]
  );

  const articleDisplayTitle = parsedArticle.title || selectedArticle?.title || '';

  const pushNavEntry = useCallback((entry) => {
    navStackRef.current.push(entry);
  }, []);

  const clearNavStack = useCallback(() => {
    navStackRef.current = [];
  }, []);

  const restoreNavEntry = useCallback((entry) => {
    setArticleError(null);
    setScrollToSection(null);
    if (entry.type === 'index') {
      setSelectedArticleId(null);
      setArticleMarkdown('');
      setActiveTab(entry.activeTab);
      setSearchQuery(entry.searchQuery);
    } else {
      setSelectedArticleId(entry.articleId);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToHelpIndex = useCallback(() => {
    clearNavStack();
    setSelectedArticleId(null);
    setArticleMarkdown('');
    setArticleError(null);
    setScrollToSection(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [clearNavStack]);

  const goBack = useCallback(() => {
    const stack = navStackRef.current;
    if (stack.length === 0) {
      goToHelpIndex();
      return;
    }
    const entry = stack.pop();
    navStackRef.current = stack;
    restoreNavEntry(entry);
  }, [goToHelpIndex, restoreNavEntry]);

  const openArticle = useCallback(
    (articleId) => {
      if (!articleId || articleId === selectedArticleId) return;

      if (selectedArticleId) {
        pushNavEntry({ type: 'article', articleId: selectedArticleId });
      } else {
        pushNavEntry({ type: 'index', activeTab, searchQuery });
      }

      setSelectedArticleId(articleId);
      setArticleError(null);
      setScrollToSection(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [activeTab, searchQuery, selectedArticleId, pushNavEntry]
  );

  const navigateToHelpSection = useCallback(
    (section) => {
      if (selectedArticleId) {
        pushNavEntry({ type: 'article', articleId: selectedArticleId });
      }
      setSelectedArticleId(null);
      setArticleMarkdown('');
      setArticleError(null);
      setSearchQuery('');
      setScrollToSection(section);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [selectedArticleId, pushNavEntry]
  );

  const navigateToHelpHome = useCallback(() => {
    if (selectedArticleId) {
      pushNavEntry({ type: 'article', articleId: selectedArticleId });
    }
    setSelectedArticleId(null);
    setArticleMarkdown('');
    setArticleError(null);
    setActiveTab(TAB_FOR_YOU);
    setSearchQuery('');
    setScrollToSection(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedArticleId, pushNavEntry]);

  useEffect(() => {
    if (!scrollToSection || selectedArticleId) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(`help-group-${scrollToSection}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setScrollToSection(null);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [scrollToSection, selectedArticleId, activeTab]);

  useEffect(() => {
    if (!selectedArticle) {
      setArticleMarkdown('');
      return undefined;
    }

    let cancelled = false;
    setArticleLoading(true);
    setArticleError(null);

    (async () => {
      try {
        const md = await fetchHelpArticleMarkdown(selectedArticle.path);
        if (!cancelled) {
          setArticleMarkdown(md);
        }
      } catch (err) {
        if (!cancelled) {
          setArticleError(err.message || 'Failed to load article.');
          setArticleMarkdown('');
        }
      } finally {
        if (!cancelled) {
          setArticleLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedArticle]);

  if (loadError) {
    return (
      <div className="help-page">
        <div className="help-page-header">
          <h1>Help</h1>
        </div>
        <div className="help-page-error" role="alert">
          <p>{loadError}</p>
        </div>
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className="help-page">
        <div className="help-page-header">
          <h1>Help</h1>
        </div>
        <div className="help-page-loading">Loading help content…</div>
      </div>
    );
  }

  if (selectedArticle) {
    return (
      <div className="help-page help-page--article">
        <div className="help-article-nav">
          <div className="help-article-nav-actions">
            <button type="button" className="help-back-btn" onClick={goBack}>
              ← Back
            </button>
            <button type="button" className="help-index-btn" onClick={goToHelpIndex}>
              Help index
            </button>
          </div>
        </div>
        <div className="help-page-header help-page-header--article">
          <h1>{articleDisplayTitle}</h1>
        </div>
        <div className="help-article-panel">
          {articleLoading && <div className="help-page-loading">Loading article…</div>}
          {articleError && (
            <div className="help-page-error" role="alert">
              <p>{articleError}</p>
            </div>
          )}
          {!articleLoading && !articleError && (
            <MarkdownHelpViewer
              markdown={parsedArticle.body}
              manifest={manifest}
              currentArticlePath={selectedArticle.path}
              onNavigateToArticle={openArticle}
              onNavigateToHelpSection={navigateToHelpSection}
              onNavigateToHelpHome={navigateToHelpHome}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="help-page">
      <div className="help-page-header">
        <div>
          <h1>Help</h1>
          <p className="help-page-subtitle">
            Guides for <strong>{roleLabel}</strong> — English only
          </p>
        </div>
      </div>

      <div className="help-toolbar">
        <div className="help-tabs" role="tablist" aria-label="Help sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_FOR_YOU}
            className={`help-tab ${activeTab === TAB_FOR_YOU ? 'is-active' : ''}`}
            onClick={() => setActiveTab(TAB_FOR_YOU)}
          >
            For you
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_ALL}
            className={`help-tab ${activeTab === TAB_ALL ? 'is-active' : ''}`}
            onClick={() => setActiveTab(TAB_ALL)}
          >
            All topics
          </button>
        </div>
        <input
          type="search"
          className="help-search"
          placeholder="Search topics…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search help topics"
        />
      </div>

      <div className="help-content">
        {activeTab === TAB_FOR_YOU && (
          <section className="help-section" aria-label="For you">
            <p className="help-section-lead">
              Recommended guides for your role, in priority order.
            </p>
            {forYouGroups.length === 0 ? (
              <p className="help-empty">No topics match your search.</p>
            ) : (
              forYouGroups.map((group) => (
                <div key={group.groupKey} id={`help-group-${group.groupKey}`} className="help-group">
                  <h2 className="help-group-title">{group.label}</h2>
                  <ul className="help-article-list help-article-list--plain">
                    {group.articles.map((article) => (
                      <li key={article.id}>
                        <button
                          type="button"
                          className="help-article-link"
                          onClick={() => openArticle(article.id)}
                        >
                          <span className="help-article-title">{article.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </section>
        )}

        {activeTab === TAB_ALL && (
          <section className="help-section" aria-label="All topics">
            {allGroups.length === 0 ? (
              <p className="help-empty">No topics match your search.</p>
            ) : (
              allGroups.map((group) => (
                <div key={group.groupKey} id={`help-group-${group.groupKey}`} className="help-group">
                  <h2 className="help-group-title">{group.label}</h2>
                  <ul className="help-article-list help-article-list--plain">
                    {group.articles.map((article) => (
                      <li key={article.id}>
                        <button
                          type="button"
                          className="help-article-link"
                          onClick={() => openArticle(article.id)}
                        >
                          <span className="help-article-title">{article.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default HelpPage;
